/**
 * Ejected from `experimental/shared/states/useRunGate.tsx` for NEX-18040: a switched-to loop's failed
 * or never-run state offers "Switch to this loop" instead of the run action — running is only legal
 * from the loop you are actually on, so a state screen for a loop you switched to must not offer to
 * start one. Every other variant still imports the shared version, which has no notion of a switch,
 * and is unaffected.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgenticRun } from '@/features/agentic-insights/components/data/types';
import type {
  ToneTokens,
  TypeTokens,
} from '@/features/agentic-insights/components/experimental/shared/tokens';
import { RunProgress, RunState } from './RunState';

interface RunGateArgs {
  type: Pick<TypeTokens, 'claim' | 'body' | 'meta'>;
  tone: Pick<ToneTokens, 'neutral'>;
  measure: string;
  run: AgenticRun | undefined;
  /** `null` means the API answered "no such run" for an id someone linked to. */
  viewedRun: AgenticRun | null | undefined;
  requestedRunId: string | undefined;
  isLoading: boolean;
  isError: boolean;
  isRunning: boolean;
  canRun: boolean;
  /** Whether the loop being read is the one the reader navigated to — running is legal only here. */
  isNavScope: boolean;
  /** The loop actually on screen — meaningless while `isNavScope`, named in a switched-to state's copy. */
  switchedLoopName: string;
  /** Named in the switched-to state screens' own way back — see `backLink` below. */
  navLoopName: string;
  onRetry: () => void;
  onReadLatest: () => void;
  onRun: () => void;
  onSwitchToLoop: () => void;
  onBackToNavLoop: () => void;
}

/**
 * Every state that is not a readable run, resolved in one place — so the readings can assume a run
 * with findings rather than each guarding for itself, and the page can assemble rather than branch.
 *
 * Returns `undefined` when there is a run worth reading, which is the caller's signal to render it.
 */
export const useRunGate = (args: RunGateArgs): ReactNode | undefined => {
  const { t } = useTranslation();
  const {
    type,
    tone,
    measure,
    run,
    viewedRun,
    requestedRunId,
    isLoading,
    isError,
    isRunning,
    canRun,
    isNavScope,
    switchedLoopName,
    navLoopName,
  } = args;
  const findings = run?.findings ?? [];

  // The only other control on a switched-to loop's run-state screen is "Switch to this loop" —
  // which leaves the nav loop permanently, rewriting the path. Without this, browser Back becomes
  // the only way home: the banner that used to carry a "Back to…" link is deliberately suppressed
  // on these same screens (their own copy already names the loop), so the screen needs its own way
  // out. `undefined` on the nav loop itself, where there is nothing to back out of.
  const backLink = isNavScope ? undefined : (
    <button
      type="button"
      onClick={args.onBackToNavLoop}
      data-testid="agentic-x-state-back"
      className="text-xs text-text-neutral-secondary underline decoration-border-neutral-bold decoration-1 underline-offset-4 hover:text-text-neutral-primary"
    >
      {t(($) => $.loopAgenticInsights.experimental.loopSwitcher.backToLoop, { loop: navLoopName })}
    </button>
  );

  if (isLoading) {
    return (
      <p className={`${type.meta} py-16`} data-testid="agentic-x-state-loading">
        {t(($) => $.loopAgenticInsights.experimental.states.loading)}
      </p>
    );
  }

  if (isError) {
    return (
      <RunState
        type={type}
        measure={measure}
        testId="agentic-x-state-read-error"
        title={t(($) => $.loopAgenticInsights.experimental.states.readErrorTitle)}
        body={t(($) => $.loopAgenticInsights.experimental.states.readErrorBody)}
        action={{
          label: t(($) => $.loopAgenticInsights.experimental.states.retry),
          onClick: args.onRetry,
        }}
      />
    );
  }

  if (requestedRunId !== undefined && viewedRun === null) {
    return (
      <RunState
        type={type}
        measure={measure}
        testId="agentic-x-state-not-found"
        title={t(($) => $.loopAgenticInsights.experimental.states.notFoundTitle)}
        body={t(($) => $.loopAgenticInsights.experimental.states.notFoundBody)}
        action={{
          label: t(($) => $.loopAgenticInsights.experimental.states.readLatestInstead),
          onClick: args.onReadLatest,
        }}
      />
    );
  }

  // `isRunning` is derived from the *nav* loop's own live run (the trigger only ever targets that
  // loop — see the cog), so it only stands in for "no run object yet, but one just started" while
  // `isNavScope`. Off nav-scope, `!run` by this point means the API answered with no run at all
  // (the earlier `isLoading` check has already passed), regardless of what the nav loop is doing —
  // trusting `isRunning` there would show a switched-to loop as generating a run nobody triggered.
  if (run?.status === 'running' || (!run && isRunning && isNavScope)) {
    // A switched-to loop generating its own run states the fact plainly — no "the agent is working
    // through the plan…" prose, which narrates work the reader did not start and is not watching.
    const runningTitle = isNavScope
      ? t(($) => $.loopAgenticInsights.experimental.states.runningTitle)
      : t(($) => $.loopAgenticInsights.experimental.states.generatingOtherLoop.title, {
          loopName: switchedLoopName,
        });
    const runningBody = isNavScope
      ? t(($) => $.loopAgenticInsights.experimental.states.runningBody)
      : undefined;

    return run ? (
      <RunProgress
        type={type}
        tone={tone}
        measure={measure}
        run={run}
        title={runningTitle}
        body={runningBody}
        extraSecondary={backLink}
      />
    ) : (
      // Unreachable off nav-scope after the guard above, so `backLink` would always be `undefined`
      // here anyway — omitted for that reason, not by oversight.
      <RunState
        type={type}
        measure={measure}
        testId="agentic-x-state-running"
        title={runningTitle}
        body={runningBody}
      />
    );
  }

  // A switched-to loop's failed or never-run state keeps this action's mechanics the same — the CTA
  // is the switch itself, since "Run the first briefing"/"Run again" would be a write against a loop
  // the reader isn't on, and the run action is never legal for anyway (see the cog) — but the title
  // and body are plain data statements rather than the nav loop's pitch: see the module header.
  const action = isNavScope
    ? canRun
      ? { onClick: args.onRun, busy: isRunning }
      : undefined
    : { onClick: args.onSwitchToLoop, busy: false };
  const switchLabel = t(($) => $.loopAgenticInsights.experimental.loopSwitcher.switchToLoop, {
    loop: switchedLoopName,
  });

  if (run?.status === 'failed') {
    const failureCode = run.failure?.code;
    return (
      <RunState
        type={type}
        measure={measure}
        testId="agentic-x-state-failed"
        title={
          isNavScope
            ? t(($) => $.loopAgenticInsights.experimental.states.failedTitle)
            : t(($) => $.loopAgenticInsights.experimental.states.failedOtherLoop.title, {
                loopName: switchedLoopName,
              })
        }
        body={
          isNavScope
            ? failureCode
              ? t(($) => $.loopAgenticInsights.experimental.states.failedBecause, {
                  reason: t(($) => $.loopAgenticInsights.run.failure[failureCode]),
                })
              : t(($) => $.loopAgenticInsights.experimental.states.failedBody)
            : undefined
        }
        action={
          action
            ? {
                ...action,
                label: isNavScope
                  ? t(($) => $.loopAgenticInsights.experimental.header.runAgain)
                  : switchLabel,
              }
            : undefined
        }
        secondary={backLink}
      />
    );
  }

  if (!run) {
    // "Takes a couple of minutes" describes the run action specifically, so it rides along only
    // when that action is actually offered here — not on the switched-to-loop CTA, which starts
    // nothing.
    const startingHere = isNavScope && action !== undefined;
    return (
      <RunState
        type={type}
        measure={measure}
        testId="agentic-x-state-no-run"
        title={
          isNavScope
            ? t(($) => $.loopAgenticInsights.experimental.states.noRun.title)
            : t(($) => $.loopAgenticInsights.experimental.states.noRunOtherLoop.title, {
                loopName: switchedLoopName,
              })
        }
        body={
          isNavScope
            ? t(($) => $.loopAgenticInsights.experimental.states.noRun.body)
            : t(($) => $.loopAgenticInsights.experimental.states.noRunOtherLoop.body)
        }
        action={
          action
            ? {
                ...action,
                label: isNavScope
                  ? t(($) => $.loopAgenticInsights.experimental.states.noRun.action)
                  : switchLabel,
              }
            : undefined
        }
        secondary={
          startingHere ? (
            <p className={type.meta}>
              {t(($) => $.loopAgenticInsights.experimental.states.noRun.aside)}
            </p>
          ) : (
            backLink
          )
        }
      />
    );
  }

  if (findings.length === 0) {
    return (
      <RunState
        type={type}
        measure={measure}
        testId="agentic-x-state-no-findings"
        title={t(($) => $.loopAgenticInsights.experimental.states.noFindingsTitle)}
        body={t(($) => $.loopAgenticInsights.experimental.states.noFindingsBody)}
        secondary={backLink}
      />
    );
  }

  return undefined;
};
